import React, { SyntheticEvent, useState } from 'react';
import { CircularProgress } from '@mui/material';
import isEqual from 'lodash/isEqual';
import { CustomIcon } from 'components/CustomIcon';
import { IconType } from 'components/CustomIcon/CustomIcon.config';
import palette from 'theme/palette';
import {
  FilterButton,
  FilterLabel,
  FilterAction,
  SwitchBox,
  SwitchLabel,
  ItemNumber,
  Badge,
  FilterLabelActions,
} from './FilterOption.styles';
import { SwitchInfoTooltip } from './SwitchInfoTooltip';
import { BootstrapTooltip } from './Tooltips';
import MBCheckbox from '../../Elements/MBCheckbox';
import MBSwitch from '../../Elements/MBSwitch/MBSwitch';
import { Typography } from '../../Typography';

type FilterDataValues = {
  active?: boolean;
  count?: number;
  label: string;
  field?: string;
};

type FilterValueProps = {
  active?: boolean;
  count?: number;
  field: string;
  id: string;
  label: string;
  parent?: string;
  value?: any;
  values?: FilterDataValues[];
  parentsLabel?: string;
  high?: number;
  low?: number;
  visible?: boolean;
  collapse?: boolean;
  multiple?: string;
};

type FilterItemOptionProps = {
  item?: FilterValueProps;
  label?: string;
  count?: number;
  badgeNumber?: number;
  isSelected: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  isChild?: boolean;
  isSwitch?: boolean;
  checkboxVariant?: 'square' | 'circle';
  marginBottom?: number;
  rightIcon?: IconType;
  buttonId?: string;
  children?: React.ReactNode;
  customSwitchClass?: string;
  switchTooltipContent?: string;
  onClick: (item: any, event: SyntheticEvent) => void;
  onCheckboxClick?: (item: any, event: SyntheticEvent) => void;
  onLabelClick?: (item: any, event: SyntheticEvent) => void;
};

export const FilterOption = React.memo(
  ({
    isLoading,
    isSelected,
    item,
    label,
    count,
    disabled,
    isChild,
    checkboxVariant,
    rightIcon,
    isSwitch = false,
    marginBottom = 18,
    buttonId = undefined,
    badgeNumber,
    customSwitchClass,
    children,
    switchTooltipContent,
    onClick,
    onCheckboxClick,
    onLabelClick,
  }: FilterItemOptionProps) => {
    const textLabel = label || item?.label;
    const itemCount = count || item?.count;
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const handleClick = (event: React.SyntheticEvent) => {
      onClick?.(item, event);
    };

    const handleOnCheckboxClick = (event: React.SyntheticEvent) => {
      onCheckboxClick?.(item, event);
    };

    const handleOnLabelClick = (event: React.SyntheticEvent) => {
      onLabelClick?.(item, event);
    };

    const handleClose = () => {
      setTooltipOpen(false);
    };

    const handleOpen = (e: any) => {
      setTooltipOpen(e.target?.offsetWidth < e.target?.scrollWidth);
    };

    if (isSwitch)
      return (
        <SwitchBox
          className={customSwitchClass}
          id={buttonId}
          disabled={disabled}
          marginBottom={marginBottom}
          onClick={handleClick}
        >
          <SwitchLabel fontWeight={400} color={palette.primary.main}>
            {children || textLabel}
            {switchTooltipContent && (
              <SwitchInfoTooltip switchTooltipContent={switchTooltipContent} />
            )}
          </SwitchLabel>
          {isLoading ? <CircularProgress /> : <MBSwitch checked={isSelected} />}
        </SwitchBox>
      );

    return (
      <BootstrapTooltip
        open={tooltipOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        title={textLabel}
        placement="right"
      >
        <FilterButton
          id={buttonId}
          onClick={handleClick}
          disabled={disabled}
          ischild={isChild}
          marginBottom={marginBottom}
        >
          <FilterAction isSelected={isSelected}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <MBCheckbox
                onClick={handleOnCheckboxClick}
                variant={checkboxVariant}
                hovered
                checked={isSelected}
                backgroundColor={palette.grey[300]}
              />
            )}
          </FilterAction>
          <FilterLabel onClick={handleOnLabelClick}>
            <Typography textAlign="left" className="filterName">
              {children || textLabel}
            </Typography>
            <FilterLabelActions>
              {(badgeNumber ?? 0) > 0 && <Badge>{badgeNumber}</Badge>}
              {itemCount != null && (
                <ItemNumber variant="micro" pl={1}>
                  {itemCount}
                </ItemNumber>
              )}
              {rightIcon && <CustomIcon name={rightIcon} dense size={0.8} />}
            </FilterLabelActions>
          </FilterLabel>
        </FilterButton>
      </BootstrapTooltip>
    );
  },
  isEqual,
);

FilterOption.displayName = 'FilterOption';
